/**
 * Fire-and-forget analytics primitives shared by `sign_up` and paid `purchase`.
 *
 * Ad blockers stub or throw from `dataLayer.push`, `gtag`, and GTM script
 * injection. None of those may reject a signup / checkout success path.
 * Never await a Google pixel, fetch, or GTM callback from here.
 */

export type AnalyticsStorage = {
    getItem(key: string): string | null;
    setItem(key: string, value: string): void;
    removeItem(key: string): void;
};

export type AnalyticsEmitDeps = {
    pushDataLayer?: (event: Record<string, unknown>) => void;
    gtag?: (...args: unknown[]) => void;
};

/**
 * GTM container present → dataLayer custom event only.
 * Direct GA4 (no GTM) → also send gtag(). Never both (GTM's Google tag would double-count).
 */
export function planGtmOrGtagEmission(input: { gtmId?: string; gaMeasurementId?: string }): { sendGtag: boolean } {
    const useGtm = Boolean(input.gtmId);
    return { sendGtag: !useGtm && Boolean(input.gaMeasurementId) };
}

/** Swallow any analytics throw so the caller can continue (redirect, persist, OAuth). */
export function afterSuccessTrack(track: () => void): void {
    try {
        track();
    } catch {
        // ad blocker, missing gtag, quota-exceeded storage, …
    }
}

/**
 * Models the signup / checkout success path: analytics must not prevent the next step.
 * Always returns `success` even when `track` throws or dataLayer is missing.
 */
export function runSuccessPathWithAnalytics<T>(success: T, track: () => void): T {
    afterSuccessTrack(track);
    return success;
}

/** Push / gtag for a planned event. Returns false when blocked or missing; never throws. */
export function emitPlannedEventSafely(
    deps: AnalyticsEmitDeps,
    plan: { dataLayer: Record<string, unknown>; sendGtag: boolean },
    gtagEvent?: { name: string; params: Record<string, unknown> }
): boolean {
    try {
        if (typeof deps.pushDataLayer !== "function") {
            return false;
        }
        deps.pushDataLayer(plan.dataLayer);
        if (plan.sendGtag && typeof deps.gtag === "function" && gtagEvent) {
            deps.gtag("event", gtagEvent.name, gtagEvent.params);
        }
        return true;
    } catch {
        return false;
    }
}

export function readStorageItem(storage: AnalyticsStorage | null | undefined, key: string): string | null {
    if (!storage) {
        return null;
    }
    try {
        return storage.getItem(key);
    } catch {
        return null;
    }
}

export function writeStorageItem(storage: AnalyticsStorage | null | undefined, key: string, value: string): void {
    if (!storage) {
        return;
    }
    try {
        storage.setItem(key, value);
    } catch {
        // private mode / blocked storage
    }
}

export function removeStorageItem(storage: AnalyticsStorage | null | undefined, key: string): void {
    if (!storage) {
        return;
    }
    try {
        storage.removeItem(key);
    } catch {
        // private mode / blocked storage
    }
}

export function normalizeAnalyticsEmail(email?: string | null): string | undefined {
    if (!email) {
        return undefined;
    }
    const trimmed = email.trim().toLowerCase();
    return trimmed.length > 0 ? trimmed : undefined;
}
